import React from 'react';
import { Link } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { QuickSuggestionsBlock } from '../components/chat/QuickSuggestionsBlock';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { CanonicalTag } from '@/components/common/CanonicalTag';
import { getServices } from '@/config/services.config';

interface ServicesIndexPageProps {
    showBanner: () => void;
}

const faqItems = [
    {
        question: 'What vehicle services are available in Moose Jaw?',
        answer: 'Moose Jaw has providers for auto insurance, vehicle financing, and other vehicle-related services for drivers across southern Saskatchewan.',
    },
    {
        question: 'Do I need auto insurance before buying a vehicle?',
        answer: 'In most cases, drivers arrange insurance coverage before completing a vehicle purchase or immediately after taking delivery.',
    },
    {
        question: 'Can I get vehicle financing with bad credit?',
        answer: 'Some financing providers work with drivers who have bad credit or limited credit history by connecting them with lenders that offer flexible approval options.',
    },
    {
        question: 'How do I find vehicle services near me?',
        answer: 'Browse the service providers listed below or use the chat to get personalized help finding insurance, financing, or other vehicle services near you.',
    },
];

export const ServicesIndexPage: React.FC<ServicesIndexPageProps> = ({ showBanner }) => {
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat();
    const services = getServices();

    const chatSuggestions = [
        'Find auto insurance near me',
        'Get vehicle financing help',
        'Find services near me',
        'Help me compare vehicle options',
    ];

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
    };

    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 px-2 sm:px-4">
                    <div className="max-w-3xl mx-auto"><LoadingState message="Loading..." /></div>
                </div>
                <Footer />
            </>
        );
    }

    // Group by serviceType
    const byType = services.reduce<Record<string, typeof services>>((acc, s) => {
        if (!acc[s.serviceType]) acc[s.serviceType] = [];
        acc[s.serviceType].push(s);
        return acc;
    }, {});

    return (
        <>
            <CanonicalTag href="https://getdrivesource.com/services" />
            <SchemaMarkup schema={faqSchema} />
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        <article className="mb-6">

                            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-2">
                                Vehicle Services
                                <span className="block text-sm sm:text-base md:text-lg font-medium text-gray-400 mt-1">
                                    Insurance, Financing, and More for Saskatchewan Drivers
                                </span>
                            </h1>

                            <div className="mb-4">
                                <p className="text-sm sm:text-base text-gray-300 mb-3">
                                    Owning a vehicle involves more than just the purchase. Drivers across southern Saskatchewan work with service providers for auto insurance, vehicle financing, and ongoing maintenance needs. Browse the providers below or chat for personalized help.
                                </p>
                            </div>

                            {/* Service providers grouped by type */}
                            {Object.entries(byType).map(([type, providers]) => (
                                <section key={type} className="mb-6">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3 border-b border-gray-700 pb-2">
                                        {type}
                                    </h2>
                                    <div className="space-y-3">
                                        {providers.map((s, i) => (
                                            <Link
                                                key={i}
                                                to={`/services/${s.slug}`}
                                                className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-[#1a1a1a] border border-gray-700 hover:border-cyan-500 transition-colors group"
                                            >
                                                <div>
                                                    <p className="text-sm sm:text-base font-semibold text-white group-hover:text-cyan-400 transition-colors">
                                                        {s.name}
                                                    </p>
                                                    <p className="text-xs sm:text-sm text-gray-400 mt-0.5">
                                                        {s.city}, {s.province}
                                                    </p>
                                                </div>
                                                <span className="text-gray-500 group-hover:text-cyan-400 transition-colors text-lg ml-4">→</span>
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            ))}

                            {/* FAQ */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Frequently Asked Questions
                                </h2>
                                <div className="space-y-4">
                                    {faqItems.map((item, i) => (
                                        <div key={i}>
                                            <h3 className="text-sm sm:text-base font-semibold text-gray-200 mb-1">{item.question}</h3>
                                            <p className="text-sm sm:text-base text-gray-300">{item.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Chat CTA */}
                            <QuickSuggestionsBlock
                                topLine="Need help?"
                                title="Find Vehicle Services Near You"
                                suggestions={chatSuggestions}
                                onSend={sendMessage}
                            />

                        </article>

                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
